import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Crown, History, ReceiptText, Star, Trash2 } from "lucide-react";

import { VipOrbAvatar } from "@/components/VipOrbAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
  createVipCheckoutSession,
  deleteVipChatSession,
  fetchVipChatSessions,
  fetchVipMemberAccount,
  fetchVipPurchaseHistory,
  updateVipChatSession,
} from "@/lib/api";
import { downloadBillingCsv, downloadInvoicePdf, downloadSignedBillingBundle, printInvoiceView } from "@/lib/billingExports";
import { pickText } from "@/lib/content";
import { KONSHUS_NAME, KONSHUS_POSTER_URL, KONSHUS_VIDEO_URL } from "@/lib/vip";

export const VipAccountPage = ({ language, vipEmail }) => {
  const [account, setAccount] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});

  const analytics = useMemo(() => {
    const settledPurchases = purchases.filter((purchase) => purchase.payment_status === "paid").length;
    const pendingPurchases = purchases.filter((purchase) => purchase.payment_status !== "paid").length;
    const totalBilledValue = purchases.reduce((sum, purchase) => sum + Number(purchase.amount || 0), 0);
    const averageMessages = sessions.length
      ? Math.round(sessions.reduce((sum, session) => sum + Number(session.message_count || 0), 0) / sessions.length)
      : 0;
    return {
      totalBilledValue,
      totalBilled: `$${totalBilledValue.toFixed(2)}`,
      settledPurchases,
      pendingPurchases,
      renewalRisk: account?.renewal_level || "stable",
      savedChats: sessions.length,
      favoriteChats: sessions.filter((session) => session.favorite).length,
      averageMessages,
    };
  }, [account, purchases, sessions]);

  useEffect(() => {
    const loadAccount = async () => {
      try {
        setLoading(true);
        const [accountData, purchaseData, sessionData] = await Promise.all([
          fetchVipMemberAccount(vipEmail),
          fetchVipPurchaseHistory(vipEmail),
          fetchVipChatSessions(vipEmail),
        ]);
        setAccount(accountData);
        setPurchases(purchaseData);
        setSessions(sessionData);
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load the member account right now.", "Impossibile caricare l'account membro in questo momento."));
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [language, vipEmail]);

  const startRenewal = async () => {
    try {
      const data = await createVipCheckoutSession({ email: vipEmail, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to open renewal checkout.", "Impossibile aprire il checkout di rinnovo."));
    }
  };

  const saveSession = async (sessionId) => {
    try {
      const title = editing[sessionId];
      const updated = await updateVipChatSession(sessionId, vipEmail, { title });
      setSessions((current) => current.map((entry) => (entry.id === sessionId ? updated : entry)));
      toast.success(pickText(language, "Conversation saved.", "Conversazione salvata."));
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to update the conversation.", "Impossibile aggiornare la conversazione."));
    }
  };

  const toggleFavorite = async (session) => {
    try {
      const updated = await updateVipChatSession(session.id, vipEmail, { favorite: !session.favorite });
      setSessions((current) => current.map((entry) => (entry.id === session.id ? updated : entry)));
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to update favorites.", "Impossibile aggiornare i preferiti."));
    }
  };

  const removeSession = async (sessionId) => {
    try {
      await deleteVipChatSession(sessionId, vipEmail);
      setSessions((current) => current.filter((entry) => entry.id !== sessionId));
      toast.success(pickText(language, "Conversation removed.", "Conversazione rimossa."));
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to delete the conversation.", "Impossibile eliminare la conversazione."));
    }
  };

  return (
    <div className="space-y-10" data-testid="vip-account-page">
      <div className="max-w-4xl space-y-4">
        <p className="section-label" data-testid="vip-account-eyebrow">
          {pickText(language, "Member Account", "Account Membro")}
        </p>
        <h2 className="text-5xl text-foreground md:text-6xl" data-testid="vip-account-title">
          {pickText(language, `Manage ${KONSHUS_NAME}, renewal, purchases, and saved conversations.`, `Gestisci ${KONSHUS_NAME}, rinnovo, acquisti e conversazioni salvate.`)}
        </h2>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="space-y-6">
          {account ? (
            <div
              className={`glass-card rounded-[2rem] p-5 ${account.renewal_level === "critical" ? "border-red-400/30" : account.renewal_level === "warning" ? "border-amber-300/30" : "border-white/10"}`}
              data-testid="vip-account-renewal-banner"
            >
              <p className="section-label">{pickText(language, "Billing Alert", "Avviso di Fatturazione")}</p>
              <p className="mt-3 text-sm leading-7 text-slate-300">
                {account.renewal_level === "critical"
                  ? pickText(language, `Renew soon — access has ${account.days_until_renewal} days remaining.`, `Rinnova presto — l'accesso ha ${account.days_until_renewal} giorni rimanenti.`)
                  : account.renewal_level === "warning"
                    ? pickText(language, `Your renewal window is approaching in ${account.days_until_renewal} days.`, `La tua finestra di rinnovo si avvicina tra ${account.days_until_renewal} giorni.`)
                    : pickText(language, `Billing is healthy. ${account.days_until_renewal} days remain before renewal.`, `La fatturazione è stabile. Restano ${account.days_until_renewal} giorni prima del rinnovo.`)}
              </p>
            </div>
          ) : null}

          <div className="glass-card rounded-[2rem] p-6" data-testid="vip-account-profile-card">
            <div className="rounded-[1.5rem] border border-white/10 bg-black/20 py-4" data-testid="vip-account-avatar-shell">
              <VipOrbAvatar name={KONSHUS_NAME} posterSrc={KONSHUS_POSTER_URL} styleKey="keeper" videoSrc={KONSHUS_VIDEO_URL} />
            </div>
            <div className="mt-5 space-y-2">
              <p className="section-label">{pickText(language, "Avatar identity", "Identità dell'avatar")}</p>
              <h3 className="text-4xl text-foreground" data-testid="vip-account-avatar-name">{KONSHUS_NAME}</h3>
              <p className="text-sm text-slate-400" data-testid="vip-account-email-text">{vipEmail}</p>
            </div>
          </div>

          <div className="glass-card rounded-[2rem] p-6" data-testid="vip-account-renewal-card">
            <div className="flex items-center gap-3 text-primary">
              <CalendarClock className="h-5 w-5" />
              <p className="section-label">{pickText(language, "Renewal Center", "Centro Rinnovo")}</p>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-status-card">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Status</p>
                <p className="mt-3 text-3xl text-foreground">{loading ? "—" : account?.status}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-renewal-date-card">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Renewal date", "Data di rinnovo")}</p>
                <p className="mt-3 text-sm text-slate-300">{loading ? "—" : account?.access_expires_at || "—"}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-payment-status-card">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Latest payment", "Ultimo pagamento")}</p>
                <p className="mt-3 text-sm text-slate-300">{loading ? "—" : account?.latest_payment_status || "—"}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-receipt-reference-card">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Receipt ref", "Rif. ricevuta")}</p>
                <p className="mt-3 text-xs text-slate-300">{loading ? "—" : account?.receipt_reference || "—"}</p>
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-purchase-count-card">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Purchases", "Acquisti")}</p>
                <p className="mt-3 text-3xl text-foreground">{loading ? "—" : account?.purchase_count}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-chat-count-card">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Saved chats", "Chat salvate")}</p>
                <p className="mt-3 text-3xl text-foreground">{loading ? "—" : account?.saved_chat_count}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-favorite-count-card">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Favorites", "Preferiti")}</p>
                <p className="mt-3 text-3xl text-foreground">{loading ? "—" : account?.favorite_chat_count}</p>
              </div>
            </div>
            <Button className="ritual-button mt-5 h-auto rounded-full" data-testid="vip-account-renew-button" onClick={startRenewal} type="button">
              <Crown className="h-4 w-4" />
              {pickText(language, "Renew annual access", "Rinnova accesso annuale")}
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-card rounded-[2rem] p-6" data-testid="vip-account-history-card">
            <div className="flex flex-wrap items-center justify-between gap-3 text-primary">
              <div className="flex items-center gap-3">
                <ReceiptText className="h-5 w-5" />
                <p className="section-label">{pickText(language, "Purchase History", "Cronologia Acquisti")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="ghost-button px-4 py-2" data-testid="vip-account-export-csv-button" onClick={() => downloadBillingCsv({ account, purchases })} type="button">
                  {pickText(language, "Export CSV", "Esporta CSV")}
                </button>
                <button className="ghost-button px-4 py-2" data-testid="vip-account-export-archive-button" onClick={() => downloadSignedBillingBundle({ account, purchases, analytics })} type="button">
                  {pickText(language, "Download archive bundle", "Scarica bundle archivio")}
                </button>
              </div>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="vip-account-analytics-grid">
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-analytics-total-billed">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Total billed", "Totale fatturato")}</p>
                <p className="mt-3 text-3xl text-foreground">{analytics.totalBilled}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-analytics-settled">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Settled purchases", "Acquisti saldati")}</p>
                <p className="mt-3 text-3xl text-foreground">{analytics.settledPurchases}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-analytics-pending">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Pending ledger", "Registro in attesa")}</p>
                <p className="mt-3 text-3xl text-foreground">{analytics.pendingPurchases}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="vip-account-analytics-messages">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Avg chat depth", "Profondità media chat")}</p>
                <p className="mt-3 text-3xl text-foreground">{analytics.averageMessages}</p>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {purchases.map((purchase) => (
                <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid={`vip-account-purchase-${purchase.id}`} key={purchase.id}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{purchase.payment_status}</p>
                      <p className="mt-2 text-xl text-foreground">${purchase.amount} {purchase.currency.toUpperCase()}</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.24em] text-slate-500">{purchase.plan_name}</p>
                    </div>
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-500">{purchase.created_at}</p>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-slate-500">
                    <p>{pickText(language, "Receipt ref", "Rif. ricevuta")}: {purchase.receipt_reference}</p>
                    <p>{pickText(language, "Session", "Sessione")}: {purchase.session_id}</p>
                    <p>{pickText(language, "Access expiry", "Scadenza accesso")}: {purchase.access_expires_at || "—"}</p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.24em] text-slate-500">
                    <span className="rounded-full border border-white/10 px-3 py-2">{pickText(language, "Checkout created", "Checkout creato")}</span>
                    <span className="rounded-full border border-white/10 px-3 py-2">{purchase.payment_status}</span>
                    <span className="rounded-full border border-white/10 px-3 py-2">{purchase.access_expires_at ? pickText(language, "Access window stored", "Finestra accesso registrata") : pickText(language, "Awaiting activation", "In attesa di attivazione")}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button className="ghost-button px-4 py-2" data-testid={`vip-account-invoice-pdf-${purchase.id}`} onClick={() => downloadInvoicePdf({ account, purchase })} type="button">
                      {pickText(language, "Download PDF", "Scarica PDF")}
                    </button>
                    <button className="ghost-button px-4 py-2" data-testid={`vip-account-invoice-print-${purchase.id}`} onClick={() => printInvoiceView({ account, purchase })} type="button">
                      {pickText(language, "Print receipt", "Stampa ricevuta")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card rounded-[2rem] p-6" data-testid="vip-account-saved-chats-card">
            <div className="flex items-center gap-3 text-primary">
              <History className="h-5 w-5" />
              <p className="section-label">{pickText(language, "Saved Chats", "Chat Salvate")}</p>
            </div>
            <div className="mt-5 space-y-4">
              {sessions.map((session) => (
                <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid={`vip-account-session-${session.id}`} key={session.id}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      className="h-11 border-white/10 bg-black/20 text-white"
                      data-testid={`vip-account-session-title-${session.id}`}
                      onChange={(event) => setEditing((current) => ({ ...current, [session.id]: event.target.value }))}
                      value={editing[session.id] ?? session.title}
                    />
                    <button className="ghost-button px-4 py-2" data-testid={`vip-account-session-save-${session.id}`} onClick={() => saveSession(session.id)} type="button">
                      {pickText(language, "Save", "Salva")}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.24em] text-slate-500">
                    <span>{session.avatar_style}</span>
                    <span>{session.message_count} msgs</span>
                    <span>{session.updated_at}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button className="ghost-button px-4 py-2" data-testid={`vip-account-session-favorite-${session.id}`} onClick={() => toggleFavorite(session)} type="button">
                      <Star className="h-4 w-4" />
                      {session.favorite ? pickText(language, "Unfavorite", "Rimuovi preferito") : pickText(language, "Favorite", "Preferito")}
                    </button>
                    <Link className="ghost-button px-4 py-2" data-testid={`vip-account-session-open-${session.id}`} to={`/vip-chat?session=${session.id}`}>
                      {pickText(language, "Open", "Apri")}
                    </Link>
                    <button className="ghost-button px-4 py-2 text-red-200 hover:border-red-400/30 hover:text-red-200" data-testid={`vip-account-session-delete-${session.id}`} onClick={() => removeSession(session.id)} type="button">
                      <Trash2 className="h-4 w-4" />
                      {pickText(language, "Delete", "Elimina")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};